<script setup lang="ts">
import Check from 'assets/icons/checked.svg'
import Go from 'assets/icons/go.svg'

const { t } = useI18n()

const route = useRoute()

const introButtonConfig = computed(() => {
    return {
        type: 'internal',
        buttonType: 'secondary',
        active: true,
        large: true,
        center: true,
        link: 'form',
        tracking: [
            { track: 'click' },
            { 'track-position': 2 },
            { 'track-name': t('home.infos.cta') },
            { 'track-url': route.fullPath }
        ]
    }
})

const sectionTrackingAttributes = computed(() => {
    return [
        { track: 'view' },
        { 'track-position': 3 },
        { section: 'home-infos', },
        { 'track-name': t('home.infos.headline') },
        { 'track-url': route.fullPath }
    ]
})

const sectionTracking = computed(() => {
    if (!sectionTrackingAttributes.value) return {}

    const attributes = {}

    sectionTrackingAttributes.value.forEach((obj) => {
        for (const [key, value] of Object.entries(obj)) {
            attributes[`data-${key}`] = value
        }
    })
    return attributes
})
</script>

<template>
    <section class="home-infos-wrapper" v-bind="sectionTracking">
        <div class="home-infos-container">
            <ContentHeadline :content-headline-config="{ title: t('home.infos.headline') }" />
            <p v-html="t('home.infos.copy1')" />

            <ul class="home-infos-list">
                <li class="home-infos-list-item">
                    <picture>
                        <RespImgSrc
                            tag-type="source"
                            image-src="/missing-children/missing-children-form.png"
                            :srcset="[244, 200, 147]"
                            :dpr="[1]"
                            :format="['avif', 'webp', 'jpg']"
                            :width="244"
                            :height="345"
                            sizes="(min-width: 1440px) 244px, (min-width: 1024px) calc((92vw - 240px) / 4), (min-width: 768px) calc((92vw - 120px) / 4), 244px"
                        />
                        <RespImgSrc
                            tag-type="img"
                            image-src="/missing-children/missing-children-form.png"
                            :srcset="[244]"
                            :dpr="[1]"
                            sizes="244px"
                            :format="['jpg']"
                            :width="244"
                            :height="345"
                            :additional-attrs="{ loading: 'lazy', alt: t('home.infos.reach.altText1') }"
                        />
                    </picture>
                    <Go class="go-icon" />
                </li>
                <li class="home-infos-list-item">
                    <h3>{{ t('home.infos.reach.headline2') }}</h3>
                    <picture>
                        <RespImgSrc
                            tag-type="source"
                            image-src="/missing-children/missing-children-social.png"
                            :srcset="[244, 200, 147]"
                            :dpr="[1]"
                            :format="['avif', 'webp', 'jpg']"
                            :width="244"
                            :height="483"
                            sizes="(min-width: 1440px) 244px, (min-width: 1024px) calc((92vw - 240px) / 4), (min-width: 768px) calc((92vw - 120px) / 4), 244px"
                        />
                        <RespImgSrc
                            tag-type="img"
                            image-src="/missing-children/missing-children-social.png"
                            :srcset="[244]"
                            :dpr="[1]"
                            sizes="244px"
                            :format="['jpg']"
                            :width="244"
                            :height="483"
                            :additional-attrs="{ loading: 'lazy', alt: t('home.infos.reach.altText2') }"
                        />
                    </picture>
                    <Check class="checked-icon" />
                </li>
                <li class="home-infos-list-item">
                    <h3>{{ t('home.infos.reach.headline3') }}</h3>
                    <picture>
                        <RespImgSrc
                            tag-type="source"
                            image-src="/missing-children/missing-children-dooh.png"
                            :srcset="[244, 200, 147]"
                            :dpr="[1]"
                            :format="['avif', 'webp', 'jpg']"
                            :width="244"
                            :height="322"
                            sizes="(min-width: 1440px) 244px, (min-width: 1024px) calc((92vw - 240px) / 4), (min-width: 768px) calc((92vw - 120px) / 4), 244px"
                        />
                        <RespImgSrc
                            tag-type="img"
                            image-src="/missing-children/missing-children-dooh.png"
                            :srcset="[244]"
                            :dpr="[1]"
                            sizes="244px"
                            :format="['jpg']"
                            :width="244"
                            :height="322"
                            :additional-attrs="{ loading: 'lazy', alt: t('home.infos.reach.altText3') }"
                        />
                    </picture>
                    <Check class="checked-icon" />
                </li>
                <li class="home-infos-list-item">
                    <h3>{{ t('home.infos.reach.headline4') }}</h3>
                    <div class="retail-images">
                        <picture>
                            <RespImgSrc
                                tag-type="source"
                                image-src="/missing-children/missing-children-edeka.png"
                                :srcset="[244, 200, 147]"
                                :dpr="[1, 2]"
                                :format="['avif', 'webp', 'png']"
                                :width="244"
                                :height="256"
                                sizes="(min-width: 1440px) 244px, (min-width: 1024px) calc((92vw - 240px) / 4), (min-width: 768px) calc((92vw - 120px) / 4), 244px"
                            />
                            <RespImgSrc
                                tag-type="img"
                                image-src="/missing-children/missing-children-edeka.png"
                                :srcset="[244]"
                                :dpr="[1, 2]"
                                sizes="244px"
                                :format="['png']"
                                :width="244"
                                :height="256"
                                :additional-attrs="{ loading: 'lazy', alt: t('home.infos.reach.altText4') }"
                            />
                        </picture>
                        <picture>
                            <RespImgSrc
                                tag-type="source"
                                image-src="/missing-children/missing-children-mms.png"
                                :srcset="[244, 200, 147]"
                                :dpr="[1, 2]"
                                :format="['avif', 'webp', 'png']"
                                :width="244"
                                :height="256"
                                sizes="(min-width: 1440px) 244px, (min-width: 1024px) calc((92vw - 240px) / 4), (min-width: 768px) calc((92vw - 120px) / 4), 244px"
                            />
                            <RespImgSrc
                                tag-type="img"
                                image-src="/missing-children/missing-children-mms.png"
                                :srcset="[244]"
                                :dpr="[1, 2]"
                                sizes="244px"
                                :format="['png']"
                                :width="244"
                                :height="256"
                                :additional-attrs="{ loading: 'lazy', alt: t('home.infos.reach.altText5') }"
                            />
                        </picture>
                    </div>

                    <Check class="checked-icon" />
                </li>
            </ul>

            <p v-html="t('home.infos.copy2')" />

            <BaseButton :base-button-config="introButtonConfig">
                {{ t('home.infos.cta') }}
            </BaseButton>

            <LogoList :track-position="3" class="home-infos-logo-list" />
        </div>
    </section>
</template>

<style lang="scss">
.home-infos-wrapper {
    overflow: hidden;
    position: relative;
    grid-column: 1 / -1;
    text-align: center;

    @include fullbleed;

    &::before {
        content: '';
        position: absolute;
        bottom: 0;
        right: 0;
        width: 100%;
        height: 50%;
        pointer-events: none;
        background: $home-infos-gradient;
        z-index: 0;
    }
}

.home-infos-container {
    grid-column: 2 / -2;
    position: relative;
    z-index: 1;

    @include section-padding;

    p {
        @include copytext;

        &:last-of-type {
            max-width: 600px;
        }
    }
}

.home-infos-list {
    display: grid;
    grid-template-columns: 1fr;
    list-style-type: none;
    margin-block: px2rem(40) px2rem(20);
    align-items: center;
    padding: 0;

    @include bp(medium1) {
        grid-template-columns: 1fr 1fr 1fr;
        grid-template-rows: repeat(2, auto);
        margin-block: px2rem(60) px2rem(100);
        gap: px2rem(40);
    }

    @include bp(large1) {
        gap: px2rem(80);
    }
}

.home-infos-list-item {
    position: relative;
    max-width: 300px;
    margin: 0 auto px2rem(40);

    &:first-child {
        margin-block-end: px2rem(100);

        @include bp(medium1) {
            margin: 0;
            grid-row: 1;
            grid-column: 1 / -1;
            text-align: center;
            display: flex;
            justify-content: center;
        }
    }

    @include bp(medium1) {
        max-width: 100%;
        margin: 0;
        grid-row: 2;
    }

    h3 {
        font-size: px2rem(14);
        margin-block-end: px2rem(20);
        font-weight: normal;
    }

    img {
        max-width: 100%;
        height: auto;
        width: auto;
        display: block;
        margin: 0 auto;
    }

    &:not(:first-child) {
        display: flex;
        flex-direction: column;
        align-items: center;

        > picture {
            display: flex;
            justify-content: center;
            align-items: center;
            height: 220px;

            @include bp(medium1) {
                height: 260px;
            }

            @include bp(large1) {
                height: 300px;
            }
        }

        > picture img {
            height: 100%;
            width: auto;
            max-width: 100%;
            object-fit: contain;
        }
    }
}

.checked-icon {
    width: 30px;
    height: 30px;
    fill: currentcolor;
    margin-block-start: auto;
    padding-block-start: px2rem(10);
}

.retail-images {
    display: flex;
    gap: px2rem(2);
    justify-content: center;
    align-items: center;
    margin-inline: px2rem(-20);
    height: 220px;

    @include bp(medium1) {
        height: 260px;
    }

    @include bp(large1) {
        height: 300px;
    }

    picture {
        flex: 1;
        min-width: 0;
        height: 100%;
        display: flex;
        align-items: center;
        justify-content: center;
    }

    img {
        width: 100%;
        max-height: 100%;
        height: auto;
        object-fit: contain;
        display: block;
    }
}

.go-icon {
    position: absolute;
    bottom: -40px;
    left: 50%;
    transform: translateX(-50%) rotate(90deg);
}

.home-infos-logo-list.logo-list {
    margin-top: px2rem(100);

    @include bp(medium1) {
        margin-top: px2rem(140);
    }
}
</style>
